/**
 * Reader for the CIRCAD "SOURCE LIBRARY FILE" (compact) format.
 *
 * Eighteen of the twenty stock libraries use this format, so it is where
 * essentially all of the part content lives. It is not a plain geometry dump:
 * it is a *generative* system. A library holds
 *
 *   1. alias masks        `74LS*=74*`   — part-number normalisation
 *   2. named patterns     `$BUF` …      — reusable body glyphs
 *   3. a catalogue        `7404,Totem-pole output` + a `$PATTERN,…` spec and a
 *                                         table of pins declared by side and
 *                                         offset
 *
 * One catalogue entry can therefore yield many placeable symbols: a 7404 has
 * six inverter sections, each a separate gate with its own pin numbers, plus
 * shared power pins. Reproducing that generation — rather than flattening it —
 * is what makes the library behave the way the original does.
 *
 * The geometry emitted by {@link generateSymbol} is a direct port of OG9.cpp
 * lines 660-1075, which builds symbols by writing keyword records.
 */

import {
  type ArcEntity, type CadDocument, type ComponentEntity, type Entity,
  type Layer, type LineEntity, type PadEntity, type PadType,
  type TextAnchor, type TextEntity, type Vertex,
  LayerType, defaultPcbLayers, newId,
} from '../model.ts';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Pin modifier bits. Values match `z` in OG9.cpp:668-706. */
export const PinFlag = {
  Bubble: 1,      // `'` inverted — draw a bubble instead of a leg
  Clock: 2,       // `~` edge triggered — draw the clock chevron
  NoLeg: 4,       // `@` pad only, no leg
  Power: 8,       // `$` power/ground pin
  LongLeg: 16,    // `x` Circad long leg
  OrcadLong: 32,  // `o` +.25"
  OrcadShort: 64, // `s` +.05"
} as const;

export type PinSide = 'L' | 'R' | 'T' | 'B';

export interface SourcePin {
  side: PinSide;
  /** Position along the side, in inches. */
  offset: number;
  flags: number;
  name: string;
  /** One pin number per gate/section. */
  numbers: string[];
}

export interface SourcePattern {
  name: string;
  /** Body geometry in pattern-local coordinates. */
  entities: Entity[];
  /** `BL` records — the component's pick/block region. */
  block: Vertex[];
}

/**
 * How a variant's body is produced, from the spec line's leading token:
 *
 *   `$,…`        box     — draw a generic rectangle from the bounds
 *   `$$,…`       inline  — the primitive records that follow are the body
 *   `$BUF,…`     pattern — reference a named pattern glyph
 *
 * The mode also decides whether pin *names* are labelled: the original draws
 * them only in box mode (OG9.cpp `mod == 0`), because a NAND gate needs no
 * "In"/"Out" text but a DIP package does.
 */
export type VariantMode = 'box' | 'inline' | 'pattern';

export interface SourceVariant {
  mode: VariantMode;
  /** Named pattern, or null in box/inline mode. */
  pattern: string | null;
  /** Inline body, used when `mode` is `inline`. */
  inline?: SourcePattern;
  bounds: { x1: number; y1: number; x2: number; y2: number };
  /**
   * Fifth spec field — the height of the notched "control box" at the top of a
   * box symbol. Zero means a plain rectangle.
   */
  z: number;
  /** P1..P9 templates, e.g. `U?f`, `?µF`. */
  params: string[];
  /** Trailing field: the PCB footprint this symbol maps to, e.g. `DIP14`. */
  footprint?: string;
  pins: SourcePin[];
  /** Number of gates, from the widest pin-number row. */
  sections: number;
}

export interface SourcePart {
  name: string;
  description: string;
  category: string;
  variants: SourceVariant[];
}

export interface SourceLibrary {
  name: string;
  title: string;
  kind: 'sch' | 'pcb';
  aliases: { pattern: string; target: string }[];
  patterns: Map<string, SourcePattern>;
  parts: SourcePart[];
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Detection
// ---------------------------------------------------------------------------

const SRC_HEADER = /^CIRCAD VERSION\s+([0-9.])\.([0-9])\s+--\s+SOURCE LIBRARY FILE/i;

export function isSourceLibrary(text: string): boolean {
  return SRC_HEADER.test(text.slice(0, 80));
}

// ---------------------------------------------------------------------------
// Layer mnemonics
// ---------------------------------------------------------------------------

/**
 * PCB libraries reference layers by type mnemonic rather than number, because
 * the target board's layer numbering is not known when the library is written.
 */
const LAYER_MNEMONICS: Record<string, { type: LayerType; bottom: boolean }> = {
  ST: { type: LayerType.Silk, bottom: false },
  SB: { type: LayerType.Silk, bottom: true },
  MT: { type: LayerType.Mask, bottom: false },
  MB: { type: LayerType.Mask, bottom: true },
  CT: { type: LayerType.Copper, bottom: false },
  CB: { type: LayerType.Copper, bottom: true },
  PT: { type: LayerType.Paste, bottom: false },
  PB: { type: LayerType.Paste, bottom: true },
};

/**
 * Resolve a compact-format layer token to a layer number.
 *
 * Numeric tokens are layer numbers. Mnemonics are matched by type and side
 * against the given stack, or the standard PCB stack when none is supplied —
 * returning 0 for an unresolved mnemonic would put silkscreen and SMT copper
 * on the pad master, which is both wrong and, for SMT pads, forbidden.
 *
 * Layer 0 is skipped when matching: it is the pad master and declares type
 * Silk, so a search for silkscreen would otherwise always land on it.
 */
export function resolveCompactLayer(token: string, layers?: readonly Layer[]): number {
  const t = token.trim().toUpperCase();
  if (/^-?\d+$/.test(t)) return parseInt(t, 10);

  const m = LAYER_MNEMONICS[t];
  if (!m) return 0;

  const stack = layers ?? STANDARD_PCB_LAYERS;
  const idx = stack.findIndex(
    (l, i) => i !== 0 && l.type === m.type && l.bottom === m.bottom);
  if (idx >= 0) return idx;

  // No exact side match — accept the type on either side before giving up.
  const any = stack.findIndex((l, i) => i !== 0 && l.type === m.type);
  return any >= 0 ? any : 0;
}

/** The stock PCB stack, used to resolve library mnemonics into layer numbers. */
const STANDARD_PCB_LAYERS: readonly Layer[] = defaultPcbLayers();

/** The type and side a layer number denotes in the standard stack. */
export function standardLayerRole(
  layer: number,
): { type: LayerType; bottom: boolean } | null {
  const l = STANDARD_PCB_LAYERS[layer];
  return l ? { type: l.type, bottom: l.bottom } : null;
}

/** Numeric pad type from the compact format: bit 2 selects S, low 2 bits the shape. */
export function padTypeFromCode(code: number): PadType {
  const letter = code & 4 ? 'S' : 'T';
  return `${letter}${code & 3}` as PadType;
}

// ---------------------------------------------------------------------------
// Primitive records
// ---------------------------------------------------------------------------

/**
 * Parse one compact primitive record.
 *
 * Field layouts are taken from the V3→V6 converter in OGfdi-cad03.cpp:1280-1345,
 * which enumerates every field of every record type:
 *
 *   1  layer width x1 y1 [x2 y2] [signal]              line (2 coords = continuation)
 *   2  layer width cx cy r angle sweep ecc rot [signal] arc
 *   3  layer type cx cy sizeX sizeY hole rot pin [signal]  pad
 *   4  layer height x y length angle anchor font data      text
 *
 * That is the layout CIRCAD V4 and later write, and the one the V6 source
 * libraries use. Design files written by V3 and earlier are the same records
 * with three differences, so `dialect` selects between them:
 *
 *   · a pad has no rotation field, so its pin and signal sit one place earlier;
 *   · text carries no anchor or font, so the string starts at field 7;
 *   · angles are fractions of a turn rather than degrees — 256ths for an arc's
 *     angle, sweep, eccentricity and rotation, 65536ths for text.
 *
 * The V3 import filter in the original (OGfdi-cad03.cpp) shows all three: it
 * inserts a zero angle before the pin, inserts "0 0" for anchor and font, and
 * divides the arc fields by 256 before writing degrees.
 */
export type RecordDialect = 'v3' | 'v4';

export function parsePrimitiveRecord(
  line: string,
  layers?: readonly Layer[],
  dialect: RecordDialect = 'v4',
): { entity: Entity | null; blockPoint?: Vertex; blockBounds?: boolean } {
  const isV3 = dialect === 'v3';
  // The text record's data field runs to end of line and may contain spaces,
  // so only the leading fields are split on whitespace.
  const parts = line.trim().split(/\s+/);
  const type = parts[0];
  const layerTok = parts[1] ?? '0';
  const num = (i: number): number => {
    const v = parseFloat(parts[i] ?? '0');
    return Number.isFinite(v) ? v : 0;
  };

  // `BL` records describe the block region, not drawable geometry.
  if (layerTok.toUpperCase() === 'BL') {
    if (parts.length >= 7) return { entity: null, blockPoint: { x: num(3), y: num(4) }, blockBounds: true };
    return { entity: null, blockPoint: { x: num(3), y: num(4) } };
  }

  const layer = resolveCompactLayer(layerTok, layers);

  // A signal name is the last field of a copper record, when it has one. It is
  // what makes a netlist possible: without it every piece of copper is an
  // anonymous island, and nets like GND never form.
  const sigAt = (i: number): string | undefined => {
    const s = parts[i]?.trim();
    return s ? s : undefined;
  };

  // Turns, as the V3 records store them, into degrees.
  const turn = (v: number, whole: number): number => (v / whole) * 360;

  switch (type) {
    case '1': {
      // A 4-field line is a polyline continuation vertex; the caller chains it.
      if (parts.length < 7) {
        return { entity: null, blockPoint: { x: num(3), y: num(4) } };
      }
      const e: LineEntity = {
        kind: 'line', id: newId(), layer,
        width: num(2), x1: num(3), y1: num(4), x2: num(5), y2: num(6),
      };
      const signal = sigAt(7);
      if (signal) e.signal = signal;
      return { entity: e };
    }
    case '2': {
      // A zero sweep is how a full circle is stored — the keyword format omits
      // Angle/Sweep entirely for one, and the positional format has to put
      // something in the field, so it puts zero. Taken literally it is an arc
      // of no extent, which draws as nothing: on ADV33.pcb alone that silently
      // lost 1060 of 2422 arcs.
      // V3 counts a whole turn as 256, so its 256 is the full circle that V4
      // writes as a zero sweep. Both mean "draw the lot".
      let angle = num(6);
      let sweep = num(7);
      if (isV3) {
        // A negative sweep runs the other way round; the original normalises it
        // to a positive sweep from the far end rather than carrying the sign.
        if (sweep < 0) { angle += sweep; sweep = -sweep; }
        angle = turn(angle, 256);
        sweep = turn(sweep, 256);
      }
      const e: ArcEntity = {
        kind: 'arc', id: newId(), layer,
        width: num(2), cx: num(3), cy: num(4), radius: num(5),
        angle, sweep: sweep === 0 ? 360 : sweep,
      };
      const ecc = isV3 ? num(8) / 256 : num(8);
      if (ecc) e.eccentricity = ecc;
      const rot = isV3 ? turn(num(9), 256) : num(9);
      if (rot) e.rotation = rot;
      const signal = sigAt(10);
      if (signal) e.signal = signal;
      return { entity: e };
    }
    case '3': {
      const e: PadEntity = {
        kind: 'pad', id: newId(), layer,
        type: padTypeFromCode(parseInt(parts[2] ?? '0', 10) || 0),
        x: num(3), y: num(4), sizeX: num(5), sizeY: num(6),
      };
      const hole = num(7);
      if (hole) e.hole = hole;
      // V3 has no rotation field, so the pin and signal sit one place earlier.
      // Read the other way round, a DIP's pin numbers become rotations of one
      // to fourteen degrees and its signal names become pin names — which is
      // how a board full of GND pads ends up with no GND net at all.
      if (isV3) {
        const pin = parts[8];
        if (pin) e.pin = pin;
        const signal = sigAt(9);
        if (signal) e.signal = signal;
      } else {
        const rot = num(8);
        if (rot) e.rotation = rot;
        const pin = parts[9];
        if (pin) e.pin = pin;
        const signal = sigAt(10);
        if (signal) e.signal = signal;
      }
      return { entity: e };
    }
    case '4': {
      // The string runs to the end of the line and may contain spaces, so it
      // is taken by position rather than by splitting. V3 has no anchor or
      // font, so its string starts two fields earlier — read at the V4 offset
      // the string falls off the end and every label comes out blank.
      const lead = isV3 ? 7 : 9;
      const head = line.trim().split(/\s+/, lead);
      let idx = 0;
      for (let f = 0; f < lead; f++) {
        idx = line.indexOf(head[f] ?? '', idx) + (head[f]?.length ?? 0);
      }
      const data = line.slice(idx).trim();
      const e: TextEntity = {
        kind: 'text', id: newId(), layer,
        x: num(3), y: num(4), height: num(2), text: data,
      };
      const length = num(5);
      if (length) e.length = length;
      // Text is placed in 65536ths of a turn, not the 256ths an arc uses.
      const rot = isV3 ? turn(num(6), 65536) : num(6);
      if (rot) e.rotation = rot;
      return { entity: e };
    }
    default:
      return { entity: null };
  }
}

// ---------------------------------------------------------------------------
// Library parsing
// ---------------------------------------------------------------------------

const PIN_RE = /^([LRTB])([-+]?(?:\d*\.\d+|\d+\.?\d*))([~'@$xos]*)\t/;

export function parseSourceLibrary(text: string, name = 'library'): SourceLibrary {
  const lines = text.split(/\r?\n/);
  const lib: SourceLibrary = {
    name,
    title: '',
    kind: 'sch',
    aliases: [],
    patterns: new Map(),
    parts: [],
    warnings: [],
  };

  let i = 0;
  if (!SRC_HEADER.test(lines[0] ?? '')) {
    lib.warnings.push('missing SOURCE LIBRARY FILE header');
  }
  i = 1;

  // --- Alias masks, e.g. `74LS*=74*`, until a blank line.
  while (i < lines.length && !lines[i]!.trim()) i++;
  for (; i < lines.length; i++) {
    const l = lines[i]!.trim();
    if (!l) break;
    const eq = l.indexOf('=');
    if (eq > 0) lib.aliases.push({ pattern: l.slice(0, eq), target: l.slice(eq + 1) });
  }

  // --- Named pattern definitions, until the catalogue marker.
  while (i < lines.length) {
    while (i < lines.length && !lines[i]!.trim()) i++;
    const head = lines[i];
    if (head === undefined) break;
    if (head.startsWith(',')) break;             // catalogue begins
    if (!head.startsWith('$')) { i++; continue; }

    const pname = head.slice(1).trim();
    i++;
    const pat = readPatternBody(lines, i);
    i = pat.next;
    // A bare `$` (SMD/THD) marks "no named patterns"; skip storing it.
    if (pname) lib.patterns.set(pname.toUpperCase(), { name: pname, ...pat.body });
  }

  // --- Catalogue.
  let category = '';
  let pending: { name: string; description: string }[] = [];

  for (; i < lines.length; i++) {
    const raw = lines[i]!;
    const l = raw.trimEnd();
    if (!l.trim()) { pending = []; continue; }

    if (l.startsWith(',***')) { lib.title = l.slice(4).trim(); continue; }
    if (l.startsWith(',---')) { category = l.slice(4).trim(); continue; }
    if (l === ',') continue;

    if (l.startsWith('$')) {
      // Symbol spec for the part(s) declared just above.
      const spec = readVariantSpec(l);
      i++;
      const body = readVariantBody(lines, i, spec);
      i = body.next - 1;

      if (!pending.length) {
        lib.warnings.push(`symbol spec with no preceding part name at line ${i + 1}`);
        continue;
      }
      for (const p of pending) {
        let part = lib.parts.find((x) => x.name === p.name);
        if (!part) {
          part = { name: p.name, description: p.description, category, variants: [] };
          lib.parts.push(part);
        }
        part.variants.push(body.variant);
      }
      continue;
    }

    // `partnumber,description`
    const comma = l.indexOf(',');
    const pname = comma === -1 ? l.trim() : l.slice(0, comma).trim();
    const pdesc = comma === -1 ? '' : l.slice(comma + 1).trim();
    if (pname) pending.push({ name: pname, description: pdesc });
  }

  // Libraries whose parts carry a footprint field are schematic libraries;
  // the SMD/THD catalogues have none and are footprint libraries.
  const anyPins = lib.parts.some((p) => p.variants.some((v) => v.pins.length > 0));
  lib.kind = anyPins ? 'sch' : 'pcb';

  return lib;
}

/** Read primitive records up to the next blank line or `$` header. */
function readPatternBody(
  lines: string[], start: number,
): { body: { entities: Entity[]; block: Vertex[] }; next: number } {
  const entities: Entity[] = [];
  const block: Vertex[] = [];
  let i = start;

  for (; i < lines.length; i++) {
    const l = lines[i]!;
    if (!l.trim()) break;
    if (l.startsWith('$') || l.startsWith(',')) break;

    const r = parsePrimitiveRecord(l);
    if (r.entity) entities.push(r.entity);
    // The first BL record carries the bounds; the rest are polygon vertices.
    else if (r.blockPoint && !r.blockBounds) block.push(r.blockPoint);
  }
  return { body: { entities, block }, next: i };
}

/** Parse a `$PATTERN,x1,y1,x2,y2,z,P1,P2,P3,Footprint` spec line. */
function readVariantSpec(l: string): SourceVariant {
  const comma = l.indexOf(',');
  const patRaw = (comma === -1 ? l : l.slice(0, comma)).slice(1).trim();
  const rest = comma === -1 ? '' : l.slice(comma + 1);
  const f = rest.split(',');
  const n = (k: number): number => {
    const v = parseFloat(f[k] ?? '0');
    return Number.isFinite(v) ? v : 0;
  };

  const mode: VariantMode = patRaw === '' ? 'box' : patRaw === '$' ? 'inline' : 'pattern';
  const pattern = mode === 'pattern' ? patRaw.toUpperCase() : null;

  // Fields are positional, not trailing. The original skips exactly six terms
  // ($PATTERN, x1, y1, x2, y2, z) then reads exactly four — P1, P2, P3 and the
  // footprint (OGdlg00.cpp:5953). Taking the *last* field instead meant a spec
  // with three trailing fields yielded P3 as the footprint: THD resistors
  // claimed a footprint of "1/8W" and SMD parts one of "?".
  const params = [5, 6, 7].map((k) => (f[k] ?? '').trim());
  const footprint = (f[8] ?? '').trim();

  return {
    mode,
    pattern,
    bounds: { x1: n(0), y1: n(1), x2: n(2), y2: n(3) },
    z: n(4),
    params,
    footprint: footprint || undefined,
    pins: [],
    sections: 1,
  };
}

/** Read the pin rows and/or inline geometry that follow a spec line. */
function readVariantBody(
  lines: string[], start: number, variant: SourceVariant,
): { variant: SourceVariant; next: number } {
  const inlineEntities: Entity[] = [];
  const inlineBlock: Vertex[] = [];
  let i = start;

  for (; i < lines.length; i++) {
    const l = lines[i]!;
    if (!l.trim()) break;
    if (l.startsWith('$') || l.startsWith(',')) break;

    const pin = PIN_RE.exec(l);
    if (pin) {
      const fields = l.split('\t');
      variant.pins.push({
        side: pin[1] as PinSide,
        offset: parseFloat(pin[2]!) || 0,
        flags: pinFlags(pin[3] ?? ''),
        name: (fields[1] ?? '').trim(),
        numbers: fields.slice(2).map((s) => s.trim()).filter((s) => s.length > 0),
      });
      continue;
    }

    if (/^[1-4]\s/.test(l)) {
      const r = parsePrimitiveRecord(l);
      if (r.entity) inlineEntities.push(r.entity);
      else if (r.blockPoint && !r.blockBounds) inlineBlock.push(r.blockPoint);
      continue;
    }

    // Rows that are neither pins nor primitives — part variants without a
    // leading side letter — are catalogue continuations; stop here.
    break;
  }

  if (inlineEntities.length || inlineBlock.length) {
    variant.inline = { name: '', entities: inlineEntities, block: inlineBlock };
  }
  variant.sections = variant.pins.reduce((m, p) => Math.max(m, p.numbers.length), 1);

  return { variant, next: i };
}

function pinFlags(mods: string): number {
  let z = 0;
  for (const c of mods) {
    switch (c) {
      case '~': z |= PinFlag.Clock; break;
      case "'": z |= PinFlag.Bubble; break;
      case '@': z |= PinFlag.NoLeg; break;
      case '$': z |= PinFlag.Power; break;
      case 'x': z |= PinFlag.LongLeg; break;
      case 'o': z |= PinFlag.OrcadLong; break;
      case 's': z |= PinFlag.OrcadShort; break;
      default: break;
    }
  }
  return z;
}

// ---------------------------------------------------------------------------
// Symbol generation — port of OG9.cpp:736-1075
// ---------------------------------------------------------------------------

/** Layer numbers used by generated symbols, matching the stock SCH.LIB stack. */
const SYM_LAYER = 1;   // Symbols
const NAME_LAYER = 4;  // Pin names
const NUM_LAYER = 5;   // Pin numbers

export interface GenerateOptions {
  /** Which gate/section to build, 0-based. */
  section?: number;
  /**
   * Override the pin-name labelling rule. By default names are drawn only for
   * box symbols, matching the original.
   */
  showPinNames?: boolean;
  /**
   * Layer for the body background fill. The original leaves this off unless
   * `PlaceComponent:: BgLayer:` is configured.
   */
  backgroundLayer?: number;
}

/**
 * Draw the generic body of a box symbol: a rectangle inset .05" from the
 * bounds, optionally with the notched control box across the top when the
 * variant declares a control height. Ported from OG9.cpp:205-360.
 */
function buildBoxBody(
  bounds: { x1: number; y1: number; x2: number; y2: number },
  y3: number,
  backgroundLayer: number | undefined,
): Entity[] {
  const out: Entity[] = [];
  const x1 = bounds.x1 + 0.05;
  const x2 = bounds.x2 - 0.05;
  const yb = bounds.y1 + 0.05;
  const yt = bounds.y2 - 0.05;
  // Top of the main box: below the control box when one is present.
  const ym = bounds.y2 - y3 - 0.05;
  const yc = bounds.y2 - y3;

  const seg = (ax: number, ay: number, bx: number, by: number): void => {
    out.push({
      kind: 'line', id: newId(), layer: SYM_LAYER, width: 0.01,
      x1: ax, y1: ay, x2: bx, y2: by,
    });
  };

  seg(x1, yb, x2, yb);   // bottom
  seg(x2, yb, x2, ym);   // right
  seg(x2, ym, x1, ym);   // top of main box
  seg(x1, ym, x1, yb);   // left

  if (y3) {
    // The control box sits above, joined by a stepped jog on each side.
    seg(x1 + 0.05, ym, x1 + 0.05, yc);
    seg(x1 + 0.05, yc, x1, yc);
    seg(x1, yc, x1, yt);
    seg(x1, yt, x2, yt);
    seg(x2, yt, x2, yc);
    seg(x2, yc, x2 - 0.05, yc);
    seg(x2 - 0.05, yc, x2 - 0.05, ym);
  }

  // Reference designator and value, substituted from P1/P2 at render time.
  out.push({
    kind: 'text', id: newId(), layer: SYM_LAYER,
    x: bounds.x2, y: bounds.y2, height: 0.06, length: 0.06,
    anchor: 'BL', text: '&1',
  });
  out.push({
    kind: 'text', id: newId(), layer: SYM_LAYER,
    x: bounds.x2, y: bounds.y1, height: 0.06, length: 0.06,
    anchor: 'TL', text: '&2',
  });

  if (backgroundLayer !== undefined) {
    const verts: Vertex[] = y3
      ? [
          { x: x1, y: ym }, { x: x1, y: yb }, { x: x2, y: yb }, { x: x2, y: ym },
          { x: x2 - 0.05, y: ym }, { x: x2 - 0.05, y: yc }, { x: x2, y: yc },
          { x: x2, y: yt }, { x: x1, y: yt }, { x: x1, y: yc },
          { x: x1 + 0.05, y: yc }, { x: x1 + 0.05, y: ym },
        ]
      : [{ x: x1, y: ym }, { x: x1, y: yb }, { x: x2, y: yb }, { x: x2, y: ym }];
    out.unshift({
      kind: 'fill', id: newId(), layer: backgroundLayer, width: 0,
      bounds: { x1, y1: yb, x2, y2: yt },
      vertices: verts,
    });
  }

  return out;
}

/**
 * Build a placeable component from a catalogue part.
 *
 * Geometry constants (leg lengths, bubble radius .025", label insets, .06"
 * text) are those of the original; changing them would make symbols that look
 * subtly wrong next to a legacy schematic.
 */
export function generateSymbol(
  lib: SourceLibrary,
  part: SourcePart,
  variantIndex = 0,
  opts: GenerateOptions = {},
): ComponentEntity | null {
  const variant = part.variants[variantIndex];
  if (!variant) return null;

  const section = opts.section ?? 0;
  // Pin names belong to box symbols only, unless the caller overrides.
  const showPinNames = opts.showPinNames ?? variant.mode === 'box';
  const { x1, y1, x2, y2 } = variant.bounds;
  const children: Entity[] = [];

  if (variant.mode === 'box') {
    children.push(...buildBoxBody(variant.bounds, variant.z, opts.backgroundLayer));
  } else {
    const body = variant.mode === 'pattern' && variant.pattern
      ? lib.patterns.get(variant.pattern)
      : variant.inline;
    if (body) for (const e of body.entities) children.push(cloneEntity(e));
  }

  const line = (ax: number, ay: number, bx: number, by: number): void => {
    children.push({
      kind: 'line', id: newId(), layer: SYM_LAYER, width: 0.01,
      x1: ax, y1: ay, x2: bx, y2: by,
    });
  };
  const bubble = (cx: number, cy: number): void => {
    children.push({
      kind: 'arc', id: newId(), layer: SYM_LAYER, width: 0.01,
      cx, cy, radius: 0.025, angle: 0, sweep: 360,
    });
  };
  const label = (
    layer: number, tx: number, ty: number, anchor: TextAnchor | string, text: string,
  ): void => {
    children.push({
      kind: 'text', id: newId(), layer,
      x: tx, y: ty, height: 0.06, length: 0.06,
      anchor: normaliseAnchor(anchor), text,
    });
  };
  const pad = (px: number, py: number, pin: string): void => {
    children.push({
      kind: 'pad', id: newId(), layer: 0, type: 'T0',
      x: px, y: py, sizeX: 0.01, sizeY: 0.01, pin,
    });
  };

  for (const p of variant.pins) {
    const number = p.numbers[Math.min(section, p.numbers.length - 1)];
    if (!number) continue;

    const z = p.flags;
    const pos = p.offset;
    // Leg extension: Orcad long/short, otherwise flush with the body.
    const ext = z & PinFlag.OrcadLong ? 0.25 : z & PinFlag.OrcadShort ? 0.05 : 0;
    // Vertical legs have their own base length, extended by the `x` modifier.
    const vleg = z & PinFlag.LongLeg ? 0.125 : 0.05;

    switch (p.side) {
      case 'L': {
        if (z & PinFlag.Clock) {
          line(x1 + 0.1, pos, x1 + 0.05, pos + 0.025);
          line(x1 + 0.1, pos, x1 + 0.05, pos - 0.025);
        }
        if (!(z & PinFlag.NoLeg)) {
          if (z & PinFlag.Bubble) {
            bubble(x1 + 0.025, pos);
            if (ext) line(x1, pos, x1 - ext, pos);
          } else {
            line(x1 + 0.05, pos, x1 - ext, pos);
          }
        }
        pad(x1 - ext, pos, number);
        label(NUM_LAYER, x1 + (z & PinFlag.Bubble ? -0.01 : 0.02), pos, 'OR', number);
        if (showPinNames) {
          label(NAME_LAYER, x1 + (z & PinFlag.Clock ? 0.12 : 0.07), pos, 'L', p.name);
        }
        break;
      }
      case 'R': {
        if (!(z & PinFlag.NoLeg)) {
          if (z & PinFlag.Bubble) {
            bubble(x2 - 0.025, pos);
            if (ext) line(x2, pos, x2 + ext, pos);
          } else {
            line(x2 - 0.05, pos, x2 + ext, pos);
          }
        }
        pad(x2 + ext, pos, number);
        label(NUM_LAYER, x2 - (z & PinFlag.Bubble ? -0.01 : 0.02), pos, 'OL', number);
        if (showPinNames) label(NAME_LAYER, x2 - 0.07, pos, 'R', p.name);
        break;
      }
      case 'B': {
        if (!(z & PinFlag.NoLeg)) {
          if (z & PinFlag.Bubble) {
            bubble(pos, y1 + 0.025);
            if (ext) line(pos, y1, pos, y1 - ext);
          } else {
            line(pos, y1 + vleg, pos, y1 - ext);
          }
        }
        pad(pos, y1 - ext, number);
        label(NUM_LAYER, pos - (z & PinFlag.Bubble ? 0.04 : 0.02), y1 + 0.03, 'TR', number);
        if (showPinNames) {
          label(NAME_LAYER, pos, y1 + 0.07, 'B', z & PinFlag.Power ? 'G' : p.name);
        }
        break;
      }
      case 'T': {
        if (!(z & PinFlag.NoLeg)) {
          if (z & PinFlag.Bubble) {
            bubble(pos, y2 - 0.025);
            if (ext) line(pos, y2, pos, y2 + ext);
          } else {
            line(pos, y2 - vleg, pos, y2 + ext);
          }
        }
        pad(pos, y2 + ext, number);
        label(NUM_LAYER, pos - (z & PinFlag.Bubble ? 0.04 : 0.02), y2 - 0.03, 'BR', number);
        if (showPinNames) {
          label(NAME_LAYER, pos, y2 - 0.07, 'T', z & PinFlag.Power ? 'P' : p.name);
        }
        break;
      }
    }
  }

  const params: Record<number, string> = {};
  variant.params.forEach((v, k) => { if (v) params[k + 1] = v; });

  // The reference template carries the package's *last* gate letter, so `U?f`
  // means a six-gate package. Record the size before stamping the letter for
  // the gate actually being placed — stamping overwrites the only copy.
  const packageGates = params[1] ? gateCount(params[1]) : 1;
  if (params[1]) params[1] = stampGateLetter(params[1], section);

  const comp: ComponentEntity = {
    kind: 'component',
    id: newId(),
    layer: 0,
    name: part.name,
    bounds: { x1, y1, x2, y2 },
    x: 0,
    y: 0,
    params,
    children,
  };
  if (variant.footprint) comp.footprint = variant.footprint;
  if (packageGates > 1) comp.gates = packageGates;
  return comp;
}

/**
 * Number of gates a reference template declares.
 * `U?` is one, `U?b` two, `U?f` six — the suffix is the last gate's letter.
 */
export function gateCount(template: string): number {
  const m = /\?([a-z])$/.exec(template.trim());
  if (!m) return 1;
  return m[1]!.charCodeAt(0) - 96;
}

/** Reference prefix, e.g. `U` from `U?f`. */
export function refPrefix(template: string): string {
  const q = template.indexOf('?');
  return q === -1 ? template.replace(/\d.*$/, '') : template.slice(0, q);
}

/**
 * Replace the template's max-gate letter with the letter of `section`.
 * Single-gate templates are returned unchanged.
 */
export function stampGateLetter(template: string, section: number): string {
  const t = template.trim();
  if (gateCount(t) <= 1) return t;
  return t.replace(/\?[a-z]$/, `?${String.fromCharCode(97 + section)}`);
}

/**
 * The original writes anchors like `OR`, `OL`, `L`, `B`, `TR`. Map them to the
 * nine-point anchor set; `O` is an outside/overhang prefix that positions the
 * text beyond the anchor rather than on it.
 */
function normaliseAnchor(a: string): TextAnchor {
  switch (a.toUpperCase()) {
    case 'OR': case 'MR': case 'R': return 'MR';
    case 'OL': case 'ML': case 'L': return 'ML';
    case 'T': case 'TC': return 'TC';
    case 'B': case 'BC': return 'BC';
    case 'TR': return 'TR';
    case 'TL': return 'TL';
    case 'BR': return 'BR';
    case 'BL': return 'BL';
    default: return 'MC';
  }
}

function cloneEntity(e: Entity): Entity {
  const copy = structuredClone(e);
  copy.id = newId();
  if (copy.kind === 'component') copy.children = copy.children.map(cloneEntity);
  return copy;
}

// ---------------------------------------------------------------------------
// Alias resolution
// ---------------------------------------------------------------------------

/**
 * Apply the library's alias masks to a part number.
 * `74LS00` with mask `74LS*=74*` resolves to `7400`.
 */
export function resolveAlias(lib: SourceLibrary, partNumber: string): string {
  for (const a of lib.aliases) {
    const star = a.pattern.indexOf('*');
    if (star === -1) {
      if (a.pattern.toUpperCase() === partNumber.toUpperCase()) return a.target;
      continue;
    }
    const prefix = a.pattern.slice(0, star);
    if (partNumber.toUpperCase().startsWith(prefix.toUpperCase())) {
      const tail = partNumber.slice(prefix.length);
      return a.target.replace('*', tail);
    }
  }
  return partNumber;
}
